/**
 * SEO 與動態 Meta 標籤管理工具模組
 * 提供 SPA 路由切換時即時更新 <head> 中的 Title, Description, Keywords, Open Graph, Twitter Card 與 Canonical 標籤。
 */

export interface SeoMeta {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  priority?: string;
  changefreq?: string;
  noSearch?: boolean;
}

export const DEFAULT_SEO_CONFIG = {
  siteName: "網威智慧 HiTRUST",
  defaultTitle: "HiTRUST｜金流支付與資訊安全解決方案",
  defaultDescription:
    "網威智慧 (HiTRUST) 提供業界領先的一站式多元金流支付（信用卡、行動支付、Shopify 跨境收單、方便付）與智慧資安防詐（VeriFido 無密碼驗證、Veri-id AI 設備識別防詐、3-D Secure 交易驗證與 SSL 憑證）解決方案。",
  defaultKeywords:
    "HiTRUST, 網威智慧, HiTRUSTpay, 金流支付, 跨境金流, Shopify金流, 行動支付, 信用卡收款, 3D驗證, VeriFido, Veri-id, 資安防詐, 程式碼簽章, SSL憑證",
  defaultOgImage:
    "https://www.hitrustpay.com.tw/OfficialWebsite/static/img/og-image.png",
};

/**
 * 輔助函式：建立或更新 <meta> 標籤
 */
export function setMetaTag(
  attrName: "name" | "property",
  attrValue: string,
  content: string
) {
  if (typeof document === "undefined") return;

  let element = document.querySelector(`meta[${attrName}="${attrValue}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attrName, attrValue);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

/**
 * 輔助函式：建立或更新 <link rel="canonical">
 */
export function setCanonicalUrl(url: string) {
  if (typeof document === "undefined") return;

  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", url);
}

/**
 * 依據當前路由動態更新瀏覽器 <head> 中的所有 SEO 標籤
 * @param to Vue Router Route 物件
 */
export function updatePageMeta(to: any) {
  if (typeof document === "undefined") return;

  const meta: SeoMeta = to.meta || {};
  const routeName = to.name;

  // 1. 計算 Title
  let fullTitle = DEFAULT_SEO_CONFIG.defaultTitle;
  if (routeName === "Home" || to.path === "/Home") {
    fullTitle = meta.title || DEFAULT_SEO_CONFIG.defaultTitle;
  } else if (meta.title) {
    fullTitle = `${meta.title} | ${DEFAULT_SEO_CONFIG.siteName}`;
  }
  document.title = fullTitle;

  // 2. 計算 Description 與 Keywords
  const description = meta.description || DEFAULT_SEO_CONFIG.defaultDescription;
  const keywords = meta.keywords || DEFAULT_SEO_CONFIG.defaultKeywords;

  setMetaTag("name", "description", description);
  setMetaTag("name", "keywords", keywords);

  // 3. 計算 Open Graph 標籤 (社群分享 / LINE / Facebook)
  setMetaTag("property", "og:title", fullTitle);
  setMetaTag("property", "og:description", description);
  setMetaTag("property", "og:site_name", DEFAULT_SEO_CONFIG.siteName);
  setMetaTag("property", "og:type", "website");

  // 計算當前頁面 URL
  const currentUrl = typeof window !== "undefined" ? window.location.href : "";
  if (currentUrl) {
    setMetaTag("property", "og:url", currentUrl);
    setCanonicalUrl(currentUrl);
  }

  // 4. 計算 Twitter Card 標籤
  setMetaTag("name", "twitter:card", "summary_large_image");
  setMetaTag("name", "twitter:title", fullTitle);
  setMetaTag("name", "twitter:description", description);

  // 5. 若設定 noSearch 則加入 noindex, nofollow
  if (meta.noSearch) {
    setMetaTag("name", "robots", "noindex, nofollow");
  } else {
    setMetaTag("name", "robots", "index, follow");
  }
}
